import { Fragment, useMemo, useState } from "react";

import { ChevronRight, Expand, Shrink } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { getCumplimiento } from "~/services/contabilidad/budgetVsActualService";

import type {
  BudgetVsActualGroupedData,
  BudgetVsActualValues,
} from "~/types/contabilidad";

export type BudgetVsActualScope = "mes" | "acumulado";

interface BudgetVsActualTableProps {
  data: BudgetVsActualGroupedData | null;
  isLoading: boolean;
  scope: BudgetVsActualScope;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("es-CL", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function getPair(values: BudgetVsActualValues, scope: BudgetVsActualScope) {
  return scope === "mes"
    ? { budget: values.budgetMes, real: values.realMes }
    : { budget: values.budgetAcumulado, real: values.realAcumulado };
}

// Both series are signed (ingresos +, costos/gastos -), so a positive
// desviación (real - presupuesto) is ALWAYS favorable
function ValueCells({
  values,
  scope,
  className = "",
}: {
  values: BudgetVsActualValues;
  scope: BudgetVsActualScope;
  className?: string;
}) {
  const { budget, real } = getPair(values, scope);
  const desviacion = real - budget;
  const cumplimiento = getCumplimiento(real, budget);

  return (
    <>
      <TableCell className={`text-right font-mono ${className}`}>
        {formatCurrency(budget)}
      </TableCell>
      <TableCell className={`text-right font-mono ${className}`}>
        {formatCurrency(real)}
      </TableCell>
      <TableCell
        className={`text-right font-mono ${className} ${
          desviacion < 0 ? "text-red-700" : "text-green-700"
        }`}
      >
        {formatCurrency(desviacion)}
      </TableCell>
      <TableCell className={`text-right font-mono ${className}`}>
        {cumplimiento === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          `${cumplimiento.toFixed(1)}%`
        )}
      </TableCell>
    </>
  );
}

export function BudgetVsActualTable({
  data,
  isLoading,
  scope,
}: BudgetVsActualTableProps) {
  const [expandedClases, setExpandedClases] = useState<Set<string>>(new Set());
  const [expandedSubClases, setExpandedSubClases] = useState<Set<string>>(
    new Set()
  );
  const [expandedSubClases2, setExpandedSubClases2] = useState<Set<string>>(
    new Set()
  );

  const allKeys = useMemo(() => {
    if (!data) return { clases: [], subClases: [], subClases2: [] };

    const clases: string[] = [];
    const subClases: string[] = [];
    const subClases2: string[] = [];

    data.clases.forEach((clase) => {
      clases.push(clase.clase);
      clase.subClases.forEach((sc) => {
        const scKey = `${clase.clase}-${sc.sub_clase}`;
        subClases.push(scKey);
        sc.subClases2.forEach((sc2) => {
          subClases2.push(`${scKey}-${sc2.sub_clase_2}`);
        });
      });
    });

    return { clases, subClases, subClases2 };
  }, [data]);

  const isAllExpanded =
    expandedClases.size === allKeys.clases.length &&
    expandedSubClases.size === allKeys.subClases.length &&
    expandedSubClases2.size === allKeys.subClases2.length;

  const toggleAll = () => {
    if (isAllExpanded) {
      setExpandedClases(new Set());
      setExpandedSubClases(new Set());
      setExpandedSubClases2(new Set());
    } else {
      setExpandedClases(new Set(allKeys.clases));
      setExpandedSubClases(new Set(allKeys.subClases));
      setExpandedSubClases2(new Set(allKeys.subClases2));
    }
  };

  const toggleIn = (
    set: Set<string>,
    setter: (next: Set<string>) => void,
    key: string
  ) => {
    const next = new Set(set);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    setter(next);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(10)].map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (!data || data.clases.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground">
          No hay datos para el periodo seleccionado.
        </p>
        <p className="text-sm text-muted-foreground">
          Verifica que existan cuentas mapeadas en la plantilla y un
          presupuesto para el año.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Haz clic en las filas para expandir o contraer las secciones
        </p>
        <Button variant="outline" size="sm" onClick={toggleAll}>
          {isAllExpanded ? (
            <>
              <Shrink className="mr-2 h-4 w-4" />
              Contraer Todo
            </>
          ) : (
            <>
              <Expand className="mr-2 h-4 w-4" />
              Expandir Todo
            </>
          )}
        </Button>
      </div>

      <div className="rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[44%]">Descripción</TableHead>
              <TableHead className="w-[15%] text-right">Presupuesto</TableHead>
              <TableHead className="w-[15%] text-right">Real</TableHead>
              <TableHead className="w-[15%] text-right">Desviación</TableHead>
              <TableHead className="w-[11%] text-right">% Cumpl.</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.clases.map((clase, index) => {
              const claseKey = clase.clase;
              const isClaseExpanded = expandedClases.has(claseKey);

              return (
                <Fragment key={claseKey}>
                  <TableRow
                    className="cursor-pointer bg-muted/60 duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted"
                    style={{
                      animationDelay: `${Math.min(index * 40, 400)}ms`,
                    }}
                    onClick={() =>
                      toggleIn(expandedClases, setExpandedClases, claseKey)
                    }
                  >
                    <TableCell className="py-3 text-base font-bold tracking-wide">
                      <div className="flex items-center gap-2">
                        <ChevronRight
                          className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${
                            isClaseExpanded ? "rotate-90" : ""
                          }`}
                        />
                        {clase.clase.toUpperCase()}
                      </div>
                    </TableCell>
                    <ValueCells
                      values={clase}
                      scope={scope}
                      className="text-base font-bold"
                    />
                  </TableRow>

                  {isClaseExpanded &&
                    clase.subClases.map((subClase) => {
                      const subClaseKey = `${claseKey}-${subClase.sub_clase}`;
                      const isSubClaseExpanded =
                        expandedSubClases.has(subClaseKey);

                      return (
                        <Fragment key={subClaseKey}>
                          <TableRow
                            className="cursor-pointer bg-muted/30 duration-200 animate-in fade-in-0 slide-in-from-top-1 hover:bg-muted/50"
                            onClick={() =>
                              toggleIn(
                                expandedSubClases,
                                setExpandedSubClases,
                                subClaseKey
                              )
                            }
                          >
                            <TableCell className="py-2.5 pl-6 font-semibold">
                              <div className="flex items-center gap-2">
                                <ChevronRight
                                  className={`h-4 w-4 transition-transform duration-200 ${
                                    isSubClaseExpanded ? "rotate-90" : ""
                                  }`}
                                />
                                {subClase.sub_clase}
                              </div>
                            </TableCell>
                            <ValueCells
                              values={subClase}
                              scope={scope}
                              className="font-semibold"
                            />
                          </TableRow>

                          {isSubClaseExpanded &&
                            subClase.subClases2.map((subClase2) => {
                              const subClase2Key = `${subClaseKey}-${subClase2.sub_clase_2}`;
                              const isSubClase2Expanded =
                                expandedSubClases2.has(subClase2Key);

                              return (
                                <Fragment key={subClase2Key}>
                                  <TableRow
                                    className="cursor-pointer duration-200 animate-in fade-in-0 slide-in-from-top-1 hover:bg-muted/40"
                                    onClick={() =>
                                      toggleIn(
                                        expandedSubClases2,
                                        setExpandedSubClases2,
                                        subClase2Key
                                      )
                                    }
                                  >
                                    <TableCell className="py-2 pl-10 font-medium">
                                      <div className="flex items-center gap-2">
                                        <ChevronRight
                                          className={`h-4 w-4 transition-transform duration-200 ${
                                            isSubClase2Expanded
                                              ? "rotate-90"
                                              : ""
                                          }`}
                                        />
                                        {subClase2.sub_clase_2}
                                      </div>
                                    </TableCell>
                                    <ValueCells
                                      values={subClase2}
                                      scope={scope}
                                      className="font-medium"
                                    />
                                  </TableRow>

                                  {isSubClase2Expanded &&
                                    subClase2.grupos.map(
                                      (grupo, grupoIndex) => (
                                        <Fragment
                                          key={`${subClase2Key}-grupo-${grupoIndex}`}
                                        >
                                          {grupo.grupo_cuenta_name && (
                                            <TableRow className="duration-200 animate-in fade-in-0 slide-in-from-top-1 hover:bg-transparent">
                                              <TableCell
                                                colSpan={5}
                                                className="py-1.5 pl-14 text-sm font-medium text-muted-foreground"
                                              >
                                                {grupo.grupo_cuenta_code
                                                  ? `${grupo.grupo_cuenta_code} - `
                                                  : ""}
                                                {grupo.grupo_cuenta_name}
                                              </TableCell>
                                            </TableRow>
                                          )}

                                          {grupo.accounts.map((account) => (
                                            <TableRow
                                              key={account.account_id}
                                              className="duration-200 animate-in fade-in-0 slide-in-from-top-1 hover:bg-muted/30"
                                            >
                                              <TableCell
                                                className={`py-1.5 ${
                                                  grupo.grupo_cuenta_name
                                                    ? "pl-[72px]"
                                                    : "pl-14"
                                                }`}
                                              >
                                                <span className="font-mono text-sm text-muted-foreground">
                                                  {account.account_code}
                                                </span>
                                                <span className="ml-3 text-sm">
                                                  {account.account_name}
                                                </span>
                                              </TableCell>
                                              <ValueCells
                                                values={account}
                                                scope={scope}
                                                className="text-sm"
                                              />
                                            </TableRow>
                                          ))}

                                          {grupo.grupo_cuenta_name && (
                                            <TableRow className="duration-200 animate-in fade-in-0 slide-in-from-top-1 hover:bg-transparent">
                                              <TableCell className="py-1.5 pl-14 text-sm font-semibold text-muted-foreground">
                                                Total {grupo.grupo_cuenta_name}
                                              </TableCell>
                                              <ValueCells
                                                values={grupo}
                                                scope={scope}
                                                className="text-sm font-semibold"
                                              />
                                            </TableRow>
                                          )}
                                        </Fragment>
                                      )
                                    )}
                                </Fragment>
                              );
                            })}
                        </Fragment>
                      );
                    })}

                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={5} className="h-2 bg-background p-0" />
                  </TableRow>
                </Fragment>
              );
            })}

            {/* UTILIDAD summary row */}
            <TableRow className="border-t-2 bg-muted/50 hover:bg-muted/50">
              <TableCell className="py-3 text-base font-bold tracking-wide">
                <div className="flex items-center gap-2">
                  <ChevronRight className="h-5 w-5 opacity-0" />
                  UTILIDAD
                </div>
              </TableCell>
              <ValueCells
                values={data.totals.utilidadOperacional}
                scope={scope}
                className="text-base font-bold"
              />
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
